import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Product } from '../../../modals/product.modals';
import { ClientSettingsService, ModePermissions } from '../../../services/client-settings.service';
import { FormDataService } from '../../../services/form-data.service';
import { IndustryService } from '../../../services/industry.service';
import { Customer } from '../../../services/offer-group.service';
import { ProductDbService } from '../../../services/productdb.service';
import {
  InclLineBreakup,
  PosCalculationService,
} from './pos-calculation.service';

/**
 * Shared dependency bag for the order-mode cluster (quotation/suspense/
 * wholesale mode toggling, quotation processing, and the cart->payload
 * conversion used by both new and edited quotations).
 *
 * Like OfferGroupModeService, this cluster has deep async nesting
 * (subscribe -> subscribe -> subscribe, or subscribe wrapping several
 * setTimeout/timer chains), so every field is a live getter/setter rather
 * than a snapshot value, and cross-method calls within the cluster are
 * direct sibling calls on `this` (safe precisely because reads always go
 * through the live getters).
 *
 * Methods NOT in this module's list (disableOfferGroupMode,
 * DiscountchangeCalc, getEffectivePrice, getGroupId, isInterStateTax,
 * getOverAllDiscountAmount, getFreeAmount, hasNonGSTProducts, formatDate,
 * generateQuotationPDF, generateSuspensePdf, generatePdfNew, clearOrderData,
 * resetCardDetails/resetCashDetails/resetUpiDetails/resetRedeemCreditDetails,
 * focusProductSearch, getQuotationDetails-for-edit) are exposed as plain
 * callbacks into the component's existing methods.
 */
export interface OrderModeDeps {
  getModePermissions: () => ModePermissions;

  getIsQuotationMode: () => boolean;
  setIsQuotationMode: (value: boolean) => void;

  getIsSuspenseMode: () => boolean;
  setIsSuspenseMode: (value: boolean) => void;

  getIsWholesaleMode: () => boolean;
  setIsWholesaleMode: (value: boolean) => void;

  getIsOfferGroupMode: () => boolean;

  getIsExchangeOrder: () => boolean;
  setIsExchangeOrder: (value: boolean) => void;

  getIsDeleteMode: () => boolean;
  getIsDateEditMode: () => boolean;

  getCartProducts: () => Product[];
  setCartProducts: (value: Product[]) => void;
  setProductsTemp: (value: Partial<Product[]>) => void;

  getIsSalesDiscountEditEnabled: () => boolean;

  getQuotationValidityDate: () => Date;
  setQuotationValidityDate: (value: Date) => void;

  getIsProcessingQuotation: () => boolean;
  setIsProcessingQuotation: (value: boolean) => void;

  getIsEditingQuotation: () => boolean;
  setIsEditingQuotation: (value: boolean) => void;

  getEditingQuotationUuid: () => string;
  setEditingQuotationUuid: (value: string) => void;

  getEditingQuotationNumber: () => string;
  setEditingQuotationNumber: (value: string) => void;

  getCustomerName: () => string;
  setCustomerName: (value: string) => void;

  getMobileNumber: () => string;
  setMobileNumber: (value: string) => void;

  getFormData: () => any;
  getEnableCursorFocus: () => boolean;
  getSelectedCustomer: () => Customer | null;
  getOverAllDiscountPercent: () => number;

  setShowTotalDialog: (value: boolean) => void;
  setShowTransactionDialog: (value: boolean) => void;
  setSelectedInvoiceNo: (value: string) => void;
  setRefundCashAmount: (value: number) => void;
  setIsMobileMandatory: (value: boolean) => void;
  setInvoice: (value: any) => void;

  getLoyaltyDetails: () => {
    loyaltyAmount: number;
    loyaltyPoints: number;
    customerId: number;
  };

  disableOfferGroupMode: () => void;
  discountChangeCalc: () => void;
  getEffectivePrice: (product: Product) => number;
  /** Incl-tax line value and GST split, sent as cart `inclbreakup`; null = excl price + tax */
  getInclLineBreakup?: (product: Product) => InclLineBreakup | null;
  getGroupId: () => number;
  isInterStateTax: () => boolean;
  getOverAllDiscountAmount: () => number;
  getFreeAmount: () => number;
  hasNonGSTProducts: () => boolean;
  formatDate: (date: Date) => string;
  generateQuotationPDF: (quotationData: any) => void;
  generateSuspensePdf: (suspenseData: any) => void;
  generatePdfNew: () => void;
  clearOrderData: () => void;
  resetCardDetails: () => void;
  resetCashDetails: () => void;
  resetUpiDetails: () => void;
  resetRedeemCreditDetails: () => void;
  focusProductSearch: () => void;
  scheduleOpenScannerAfterQuotation: () => void;
  getQuotationDetailsForEdit: (quotationUuid: string) => void;

  destroy$: Subject<void>;
}

/**
 * OrderModeService
 *
 * Dedicated service for quotation/suspense/wholesale mode toggling,
 * quotation processing (create/update/place-from-history), and the shared
 * cart-to-payload conversion used by both the quotation and normal order
 * flows.
 *
 * Extracted from DashboardComponent to decouple order-mode logic while
 * preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderModeService {
  constructor(
    private readonly messageService: MessageService,
    private readonly clientSettingsService: ClientSettingsService,
    private readonly formDataService: FormDataService,
    private readonly industryService: IndustryService,
    private readonly productService: ProductDbService,
    private readonly posCalculationService: PosCalculationService,
  ) {}

  toggleQuotationMode(deps: OrderModeDeps): void {
    this.clientSettingsService.loadClientSettings().subscribe(() => {
      if (!deps.getModePermissions().quotationMode) {
        this.messageService.add({
          severity: 'error',
          summary: 'Access Denied',
          detail: 'Quotation mode is not enabled for your account',
          life: 3000,
        });
        return;
      }

      // Check if exchange mode is active
      if (!deps.getIsQuotationMode() && deps.getIsExchangeOrder()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Cannot Switch Mode',
          detail: 'Please complete or cancel the exchange order first',
          life: 3000,
        });
        return;
      }

      deps.setIsQuotationMode(!deps.getIsQuotationMode());

      // Disable all other modes when enabling Quotation mode
      if (deps.getIsQuotationMode()) {
        if (deps.getIsWholesaleMode()) {
          deps.setIsWholesaleMode(false);
        }
        if (deps.getIsOfferGroupMode()) {
          deps.disableOfferGroupMode();
        }
        if (deps.getIsSuspenseMode()) {
          deps.setIsSuspenseMode(false);
          // Clean up suspense mode properties
          deps.getCartProducts().forEach((product) => {
            delete product.isgst;
            product.selected = false;
          });
          deps.setProductsTemp(deps.getCartProducts().map((product) => ({ ...product })));
        }
        deps.setQuotationValidityDate(
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        );
      }

      // Recalculate prices for existing products when mode changes
      // Only recalculate if user can edit discounts
      if (deps.getIsSalesDiscountEditEnabled()) {
        deps.discountChangeCalc();
      }

      this.updateFormDataWithQuotationMode(deps);

      this.messageService.add({
        severity: deps.getIsQuotationMode() ? 'info' : 'success',
        summary: deps.getIsQuotationMode()
          ? 'Quotation Mode Enabled'
          : 'Normal Mode Enabled',
        detail: deps.getIsQuotationMode()
          ? 'Press Alt+Q to switch back to normal mode'
          : 'Press Alt+Q to switch to quotation mode',
        life: 2000,
      });
    });
  }

  // Toggle Suspense Account Mode (Alt+M) - Hidden feature
  toggleSuspenseMode(deps: OrderModeDeps): void {
    this.clientSettingsService.loadClientSettings().subscribe(() => {
      if (!deps.getModePermissions().suspenseMode) {
        this.messageService.add({
          severity: 'error',
          summary: 'Access Denied',
          detail: 'Unknown action performed',
          life: 3000,
        });
        return;
      }

      // Check if exchange mode is active
      if (!deps.getIsSuspenseMode() && deps.getIsExchangeOrder()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Cannot Switch Mode',
          detail: 'Please complete or cancel the exchange order first',
          life: 3000,
        });
        return;
      }

      deps.setIsSuspenseMode(!deps.getIsSuspenseMode());

      // Disable all other modes when enabling Suspense mode
      if (deps.getIsSuspenseMode()) {
        if (deps.getIsQuotationMode()) {
          deps.setIsQuotationMode(false);
        }
        if (deps.getIsWholesaleMode()) {
          deps.setIsWholesaleMode(false);
        }
        if (deps.getIsOfferGroupMode()) {
          deps.disableOfferGroupMode();
        }
      }

      // Recalculate prices when toggling suspense mode (especially when disabling wholesale mode)
      // Only recalculate if user can edit discounts
      if (deps.getIsSalesDiscountEditEnabled()) {
        deps.discountChangeCalc();
      }

      this.updateFormDataWithQuotationMode(deps);

      if (deps.getIsSuspenseMode()) {
        deps.getCartProducts().forEach((product) => {
          product.isgst = 1;
          product.selected = false;
        });
      } else {
        deps.getCartProducts().forEach((product) => {
          delete product.isgst;
          product.selected = false;
        });
      }

      deps.setProductsTemp(deps.getCartProducts().map((product) => ({ ...product })));
    });
  }

  // Toggle Wholesale Mode (Alt+W)
  toggleWholesaleMode(deps: OrderModeDeps): void {
    this.clientSettingsService.loadClientSettings().subscribe(() => {
      if (!deps.getModePermissions().wholesaleMode) {
        this.messageService.add({
          severity: 'error',
          summary: 'Access Denied',
          detail: 'Wholesale mode is not enabled for your account',
          life: 3000,
        });
        return;
      }

      // Check if exchange mode is active
      if (!deps.getIsWholesaleMode() && deps.getIsExchangeOrder()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Cannot Switch Mode',
          detail: 'Please complete or cancel the exchange order first',
          life: 3000,
        });
        return;
      }

      deps.setIsWholesaleMode(!deps.getIsWholesaleMode());

      // Disable all other modes when enabling Wholesale mode
      if (deps.getIsWholesaleMode()) {
        if (deps.getIsQuotationMode()) {
          deps.setIsQuotationMode(false);
        }
        if (deps.getIsOfferGroupMode()) {
          deps.disableOfferGroupMode();
        }
        if (deps.getIsSuspenseMode()) {
          deps.setIsSuspenseMode(false);
          // Clean up suspense mode properties
          deps.getCartProducts().forEach((product) => {
            delete product.isgst;
            product.selected = false;
          });
          deps.setProductsTemp(deps.getCartProducts().map((product) => ({ ...product })));
        }
      }

      if (deps.getIsSalesDiscountEditEnabled()) {
        deps.discountChangeCalc();
      }

      this.updateFormDataWithQuotationMode(deps);

      this.messageService.add({
        severity: deps.getIsWholesaleMode() ? 'info' : 'success',
        summary: deps.getIsWholesaleMode()
          ? 'Wholesale Mode Enabled'
          : 'Normal Mode Enabled',
        detail: deps.getIsWholesaleMode()
          ? 'Press Alt+W to switch back to normal mode'
          : 'Press Alt+W to switch to wholesale mode',
        life: 2000,
      });
    });
  }

  // Update form data with current quotation, suspense, and wholesale mode
  updateFormDataWithQuotationMode(deps: OrderModeDeps): void {
    const formData = deps.getFormData();
    this.formDataService.updateFormData({
      currentDate: formData?.currentDate || new Date(),
      empCode: formData?.empCode || '',
      storeCode: formData?.storeCode || '',
      mobileNumber: formData?.mobileNumber || '',
      customerName: formData?.customerName || '',
      salesRep: formData?.salesRep || '',
      isQuotationMode: deps.getIsQuotationMode(),
      isSuspenseMode: deps.getIsSuspenseMode(),
      isWholesaleMode: deps.getIsWholesaleMode(),
      isOfferGroupMode: deps.getIsOfferGroupMode(),
      isExchangeOrder: deps.getIsExchangeOrder(),
      isDeleteMode: deps.getIsDeleteMode(),
      isDateEditMode: deps.getIsDateEditMode(),
    });
  }

  canCompleteQuotation(deps: OrderModeDeps): boolean {
    deps.setIsMobileMandatory(this.clientSettingsService.isCustomerMobileMandatory());
    return this.posCalculationService.canCompleteQuotation(
      deps.getFormData(),
      deps.getCartProducts(),
    );
  }

  // Process quotation (place order -> quotation order -> get quotation details -> generate PDF)
  processQuotation(deps: OrderModeDeps): void {
    if (!this.canCompleteQuotation(deps)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Mobile number is required for quotations',
        life: 3000,
      });
      return;
    }

    // Check if we're editing an existing quotation
    if (deps.getIsEditingQuotation() && deps.getEditingQuotationUuid()) {
      this.updateExistingQuotation(deps);
      return;
    }

    // Set loading state
    deps.setIsProcessingQuotation(true);

    // Create payload similar to existing placeOrder flow
    const payload = this.createOrderPayload(deps);

    // Step 1: Place order (same as existing flow)
    this.productService
      .placeOrder(payload)
      .pipe(takeUntil(deps.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.code === '200' && response.data?.uuid) {
            const orderUuid = response.data.uuid;

            // Step 2: Create quotation order
            this.productService
              .quotationOrder(orderUuid, deps.getQuotationValidityDate().toISOString())
              .pipe(takeUntil(deps.destroy$))
              .subscribe({
                next: (quotationResponse: any) => {
                  if (quotationResponse.code === '200') {
                    // Step 3: Get quotation details for PDF generation
                    this.productService
                      .getQuotationDetails(orderUuid)
                      .pipe(takeUntil(deps.destroy$))
                      .subscribe({
                        next: (quotationDetails: any) => {
                          if (quotationDetails.code === '200') {
                            // Step 4: Generate quotation PDF with detailed data
                            deps.generateQuotationPDF(quotationDetails.data);
                            deps.setShowTotalDialog(false);

                            // Step 5: Clear table data and reset UI
                            deps.setCartProducts([]);
                            deps.scheduleOpenScannerAfterQuotation();
                            deps.setProductsTemp([]);
                            deps.setCustomerName('');
                            deps.setMobileNumber('');
                            deps.resetCardDetails();
                            deps.resetCashDetails();
                            deps.resetUpiDetails();
                            deps.resetRedeemCreditDetails();

                            const formData = deps.getFormData();
                            this.formDataService.updateFormData({
                              currentDate: new Date(), // Reset to today's date after quotation
                              empCode: formData?.empCode || '',
                              storeCode: formData?.storeCode || '',
                              customerName: '',
                              mobileNumber: '',
                              salesRep: formData?.salesRep || '',
                            });

                            deps.setIsQuotationMode(false);
                            this.updateFormDataWithQuotationMode(deps);

                            if (deps.getEnableCursorFocus()) {
                              setTimeout(() => {
                                deps.focusProductSearch();
                              }, 100);
                            }

                            deps.setIsProcessingQuotation(false);

                            this.messageService.add({
                              severity: 'success',
                              summary: 'Quotation Generated',
                              detail:
                                'Quotation has been successfully generated and saved',
                              life: 3000,
                            });
                          }
                        },
                        error: () => {
                          deps.setIsProcessingQuotation(false);
                          this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail:
                              'Failed to retrieve quotation details for PDF generation',
                            life: 3000,
                          });
                        },
                      });
                  }
                },
                error: () => {
                  deps.setIsProcessingQuotation(false);
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to create quotation order',
                    life: 3000,
                  });
                },
              });
          }
        },
        error: (error) => {
          deps.setIsProcessingQuotation(false);

          let errorMessage = 'Failed to generate quotation';

          if (error.status === 503 && error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 0) {
            errorMessage = 'Network error: Unable to connect to server';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.statusText) {
            errorMessage = error.statusText;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Cannot Generate Quotation',
            detail: errorMessage,
            life: 5000,
          });
        },
      });
  }

  // Process suspense order (parallel to normal invoice generation)
  processSuspenseOrder(
    orderUuid: string,
    allItemsNonGST: boolean = false,
    deps: OrderModeDeps,
  ): void {
    console.log('processSuspenseOrder START', { orderUuid, allItemsNonGST });

    // Step 1: Generate suspense (always required)
    this.productService
      .generateSuspense(orderUuid)
      .pipe(takeUntil(deps.destroy$))
      .subscribe({
        next: (suspenseResponse: any) => {
          if (
            suspenseResponse.status === 'success' &&
            suspenseResponse.data?.Suspense?.uuid
          ) {
            if (allItemsNonGST) {
              // skip step 2 and 3
              deps.generateSuspensePdf(suspenseResponse.data);
              this.messageService.add({
                severity: 'success',
                summary: 'Suspense Invoice Generated',
                detail:
                  'Suspense account invoice has been generated successfully',
                life: 3000,
              });
            } else {
              // Step 2: Generate suspense invoice using orderUuid (not suspense UUID)
              this.productService
                .generateSuspenseInvoice(orderUuid)
                .pipe(takeUntil(deps.destroy$))
                .subscribe({
                  next: (invoiceResponse: any) => {
                    // Step 3: Set invoice data and generate normal PDF
                    if (invoiceResponse.code === '200') {
                      deps.setInvoice(invoiceResponse.data);
                      // MOVED TO DashboardRightPanelComponent — bill number sync no longer needed in parent
                      // this.syncBillNumberFromInvoice();
                      deps.generatePdfNew();
                    }

                    // Step 4: Generate suspense PDF from suspense data - draft PDF
                    deps.generateSuspensePdf(suspenseResponse.data);

                    deps.clearOrderData();

                    this.messageService.add({
                      severity: 'success',
                      summary: 'Suspense Invoice Generated',
                      detail:
                        'Suspense account invoice has been generated successfully',
                      life: 3000,
                    });
                  },
                  error: (error) => {
                    console.error('Suspense invoice generation failed:', error);
                    console.error('Error details:', {
                      status: error.status,
                      statusText: error.statusText,
                      message: error.message,
                      url: error.url,
                      error: error.error,
                    });

                    let errorMessage = 'Failed to generate suspense invoice';
                    if (error.status === 0) {
                      errorMessage =
                        'Network error: Unable to connect to server. Please check if backend is running.';
                    } else if (error.status === 401) {
                      errorMessage = 'Unauthorized: Please login again';
                    } else if (error.status === 500) {
                      errorMessage =
                        'Server error: ' +
                        (error.error?.message || 'Internal server error');
                    } else if (error.error?.message) {
                      errorMessage = error.error.message;
                    }

                    this.messageService.add({
                      severity: 'error',
                      summary: 'Suspense Invoice Error',
                      detail: errorMessage,
                      life: 5000,
                    });
                  },
                });
            }
          }
        },
        error: (error) => {
          console.error('generateSuspense FAILED:', error);
          console.error('Error details:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            url: error.url,
            error: error.error,
          });

          let errorMessage = 'Failed to generate suspense account';
          if (error.status === 0) {
            errorMessage =
              'Network error: Unable to connect to server. Please check if backend is running on localhost:3000';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Suspense Error',
            detail: errorMessage,
            life: 5000,
          });
        },
      });
  }

  // Reusable method to convert products to cart items
  productsToCartItems(
    products: Product[],
    isExchangeOrder: boolean = false,
    deps: OrderModeDeps,
  ): any[] {
    return products.map((obj: any) => {
      const effectivePrice = deps.getEffectivePrice(obj);
      const isgstValue =
        obj.isgst !== undefined && obj.isgst !== null ? obj.isgst : 1;

      // Always send base price without tax - backend will calculate tax
      // This applies to both normal mode and suspense mode
      const price = effectivePrice;

      let ctax = obj.ctax || 0;
      let stax = obj.stax || 0;
      let itax = obj.itax || 0;

      if (deps.getSelectedCustomer()) {
        if (deps.isInterStateTax()) {
          if (!itax || itax === 0) {
            itax = Number(ctax) + Number(stax);
          }
          ctax = 0;
          stax = 0;
        } else {
          if ((!ctax || ctax === 0) && (!stax || stax === 0) && itax > 0) {
            // If ctax/stax not set but itax is, split itax into ctax and stax
            ctax = itax / 2;
            stax = itax / 2;
          }
          itax = 0;
        }
      } else {
        // If no customer selected, assume intra-state and ensure ctax/stax are set
        if ((!ctax || ctax === 0) && (!stax || stax === 0) && itax > 0) {
          ctax = itax / 2;
          stax = itax / 2;
        }
        itax = 0;
      }

      console.log('Final tax values for cart item:', { ctax, stax, itax });
      console.log('order items ', obj)
      const cartItem: any = {
        product_id: obj.product_id,
        sku: obj.skuid,
        psid: obj.psid,
        batch: obj.batch || '',
        name: obj.name,
        retured: obj.returned || null,
        mrp: obj.sellingmrp,
        qty: isExchangeOrder
          ? Number(obj.exchangeQuantity) || 0
          : obj.increaseQuantity || 1,
        price: price, // Base price without tax - backend calculates tax
        originalprice: (obj as any).barcodesellprice || 0, // raw price - barcode selling price will never change
        groupid: deps.getGroupId(),
        tax: obj.tax,
        hsncode: obj.hsncode,
        ctax: ctax, // CGST - 0 if inter-state
        stax: stax, // SGST - 0 if inter-state
        itax: itax, // IGST - 0 if intra-state
        cess: obj.cess,
        discount: obj.discount,
        discountval: obj.discountval || 0,
        globalDiscountShare: (obj as any).globalDiscountShare || 0, // Prorated share of overall discount
        purchaseorderitem_id: obj.purchaseorderitem_id,
        isgst: isgstValue,
        // Resolve isFree from existing Mobile-industry toggle
        isFree: (Number(obj.isFree ?? (obj as any).is_free ?? (obj as any).isfree ?? 0) === 1 || obj.isFree === true) ? 1 : 0,
        is_free: (Number(obj.isFree ?? (obj as any).is_free ?? (obj as any).isfree ?? 0) === 1 || obj.isFree === true) ? 1 : 0,
        // isfree = 1 when product is full-product-free (isFree toggle) OR user has assigned free qty > 0
        isfree: (
          (Number(obj.isFree ?? (obj as any).is_free ?? (obj as any).isfree ?? 0) === 1 || obj.isFree === true) ||
          ((obj as any).cart_free_qty > 0)
        ) ? 1 : 0,
        /** Free (non-billable) quantity — user-entered value, not the total available stock */
        free_qty: Number((obj as any).cart_free_qty) || 0,
        unit_id: (obj as any).unit_id ?? (obj as any).unit ?? (obj as any).unit_mappings?.[0]?.unitid ?? null,  // selected sale unit for multi-unit products

      };

      // Incl-tax pricing: backend saves this line value and GST split as is (see placeOrder)
      const inclBreakup = isExchangeOrder ? null : deps.getInclLineBreakup?.(obj);
      if (inclBreakup) {
        cartItem.inclbreakup = inclBreakup;
      }

      // Mobile industry only: include mapping_id and selected variant (map value) details.
      // Override purchaseorderitem_id with the variant's own id so the backend
      // can identify the exact stock row. Also send the full selectedMapValue
      // so the backend has variant/parameter details for invoice generation.
      if (this.industryService.isMobile()) {
        const selMapId = obj.selectedMappingId ?? obj.mapping_id ?? null;
        cartItem.mapping_id = selMapId !== undefined && selMapId !== null ? selMapId : null;

        if (obj.returned) {
          // For sales return items: do not send selectedMapValue object, only send productmapvalue_id
          const pmvId = obj.productmapvalue_id || obj.selectedMapValue?.id || (obj as any).features?.productmapvalue_id || null;
          if (pmvId !== null && pmvId !== undefined) {
            cartItem.productmapvalue_id = pmvId;
          }
          if (obj.selectedMapValue) {
            const smv = obj.selectedMapValue;
            cartItem.purchaseorderitem_id = smv.purchaseorderitem_id || obj.purchaseorderitem_id;
            cartItem.batch = smv.batch || obj.batch || '';
          }
        } else if (obj.selectedMapValue) {
          const smv = obj.selectedMapValue;
          cartItem.purchaseorderitem_id = smv.purchaseorderitem_id || obj.purchaseorderitem_id;
          cartItem.selectedMapValue = smv; // full variant detail for backend / invoice
          cartItem.batch = smv.batch || obj.batch || ''; // prefer variant-level batch
        }
      }

      if (obj.description && obj.description.trim()) {
        cartItem.description = obj.description.trim();
      }
      if (this.industryService.isMobile() && obj.warranty_date) {
        cartItem.warranty_date = obj.warranty_date;
      }
      return cartItem;
    });
  }

  createOrderPayload(deps: OrderModeDeps): any {
    // Get shipping details from form data service
    const shippingDetails = this.formDataService.getShippingDetails();
    const formData = deps.getFormData();
    const loyaltyDetails = deps.getLoyaltyDetails();

    let payload: any = {
      mobile: formData?.mobileNumber,
      name: formData?.customerName,
      salesrep: formData?.salesRep,
      discpercentage: 0,
      discount: 0,
      overAllDiscount: deps.getOverAllDiscountAmount(),
      overAllDiscountPercent: deps.getOverAllDiscountPercent(),
      cart: [],
      upiDetails: {},
      cardDetails: {},
      cashDetails: {},
      redeemCreditDetails: {},
      loyaltyDetails: {
        loyaltyAmount: loyaltyDetails.loyaltyAmount,
        loyaltyPoints: loyaltyDetails.loyaltyPoints,
        customerId: loyaltyDetails.customerId,
      },
      validity: deps.getQuotationValidityDate().toISOString(),
      shippingaddress: shippingDetails.shippingaddress || '',
      lrno: shippingDetails.lrno || '',
      vehicleno: shippingDetails.vehicleno || '',
      transportername: shippingDetails.transportername || '',
      deliverychallan: shippingDetails.deliverychallan || '',
      invoicedate: formData?.currentDate
        ? deps.formatDate(formData.currentDate) // Use formatted date string instead of ISO
        : deps.formatDate(new Date()), // Fallback to current date
      freeAmount: deps.getFreeAmount(), // Mobile: total deducted for free products; 0 for Retail
    };

    // Add suspense flag when in suspense mode and has non-GST products
    if (deps.getIsSuspenseMode() && deps.hasNonGSTProducts()) {
      payload['suspense'] = 1;
    }

    // Use the reusable method to transform products to cart items
    payload['cart'] = this.productsToCartItems(deps.getCartProducts(), false, deps);
    return payload;
  }

  updateExistingQuotation(deps: OrderModeDeps): void {
    deps.setIsProcessingQuotation(true);
    const payload = this.createOrderPayload(deps);

    this.productService
      .updateQuotationOrder(deps.getEditingQuotationUuid(), payload)
      .pipe(takeUntil(deps.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.code === '200') {
            const quotationUuid = deps.getEditingQuotationUuid();
            const quotationNumber = deps.getEditingQuotationNumber();

            // Get updated quotation details for PDF generation
            this.productService
              .getQuotationDetails(quotationUuid)
              .pipe(takeUntil(deps.destroy$))
              .subscribe({
                next: (quotationDetails: any) => {
                  if (quotationDetails.code === '200') {
                    // Generate quotation PDF with updated data
                    deps.generateQuotationPDF(quotationDetails.data);

                    // Reset edit mode
                    deps.setIsEditingQuotation(false);
                    deps.setEditingQuotationUuid('');
                    deps.setEditingQuotationNumber('');

                    // Clear table data and reset UI
                    deps.setCartProducts([]);
                    deps.scheduleOpenScannerAfterQuotation();
                    deps.setProductsTemp([]);
                    deps.setCustomerName('');
                    deps.setMobileNumber('');
                    deps.resetCardDetails();
                    deps.resetCashDetails();
                    deps.resetUpiDetails();
                    deps.resetRedeemCreditDetails();

                    const formData = deps.getFormData();
                    this.formDataService.updateFormData({
                      currentDate: new Date(), // Reset to today's date after updating quotation
                      empCode: formData?.empCode || '',
                      storeCode: formData?.storeCode || '',
                      customerName: '',
                      mobileNumber: '',
                      salesRep: formData?.salesRep || '',
                    });

                    deps.setShowTotalDialog(false);
                    deps.setIsQuotationMode(false);
                    this.updateFormDataWithQuotationMode(deps);
                    deps.setIsProcessingQuotation(false);

                    if (deps.getEnableCursorFocus()) {
                      setTimeout(() => {
                        deps.focusProductSearch();
                      }, 100);
                    }

                    this.messageService.add({
                      severity: 'success',
                      summary: 'Quotation Updated',
                      detail: `Quotation ${quotationNumber} has been successfully updated`,
                      life: 3000,
                    });
                  }
                },
                error: (error) => {
                  deps.setIsProcessingQuotation(false);
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail:
                      'Failed to retrieve updated quotation details for PDF generation',
                    life: 3000,
                  });
                },
              });
          }
        },
        error: (error) => {
          deps.setIsProcessingQuotation(false);
          console.error('Update quotation failed:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: 'Failed to update quotation. Please try again.',
            life: 3000,
          });
        },
      });
  }

  // Edit quotation from quotation history
  editQuotation(quotation: any, deps: OrderModeDeps): void {
    if (deps.getIsExchangeOrder()) {
      deps.setIsExchangeOrder(false);
      this.updateFormDataWithQuotationMode(deps);
      deps.setSelectedInvoiceNo('');
      deps.setRefundCashAmount(0);
    }

    if (deps.getIsWholesaleMode()) {
      deps.setIsWholesaleMode(false);
    }
    if (deps.getIsOfferGroupMode()) {
      deps.disableOfferGroupMode();
    }
    if (deps.getIsSuspenseMode()) {
      deps.setIsSuspenseMode(false);
      deps.getCartProducts().forEach((product) => {
        delete product.isgst;
        product.selected = false;
      });
      deps.setProductsTemp(deps.getCartProducts().map((product) => ({ ...product })));
    }

    // Set edit mode flags
    deps.setIsEditingQuotation(true);
    deps.setEditingQuotationUuid(quotation.uuid); // Use UUID for API calls
    deps.setEditingQuotationNumber(quotation.quotationnumber); // Keep for display

    deps.setMobileNumber(quotation.user_id || '');
    deps.setCustomerName(quotation.name || '');

    const formData = deps.getFormData();
    this.formDataService.updateFormData({
      currentDate: formData?.currentDate || new Date(),
      empCode: formData?.empCode || '',
      storeCode: formData?.storeCode || '',
      customerName: deps.getCustomerName(),
      mobileNumber: deps.getMobileNumber(),
      salesRep: formData?.salesRep || '',
    });

    deps.getQuotationDetailsForEdit(quotation.uuid);

    deps.setIsQuotationMode(true);

    this.updateFormDataWithQuotationMode(deps);

    deps.setShowTransactionDialog(false);

    this.messageService.add({
      severity: 'info',
      summary: 'Edit Mode',
      detail: `Editing quotation ${quotation.quotationnumber}`,
      life: 3000,
    });
  }

  // Place order from quotation
  placeOrderFromQuotation(quotation: any, deps: OrderModeDeps): void {
    deps.setIsEditingQuotation(false);
    deps.setEditingQuotationUuid('');
    deps.setEditingQuotationNumber('');
    deps.setIsQuotationMode(false);

    deps.setMobileNumber(quotation.user_id || '');
    deps.setCustomerName(quotation.name || '');

    const formData = deps.getFormData();
    this.formDataService.updateFormData({
      currentDate: formData?.currentDate || new Date(),
      empCode: formData?.empCode || '',
      storeCode: formData?.storeCode || '',
      customerName: deps.getCustomerName(),
      mobileNumber: deps.getMobileNumber(),
      salesRep: formData?.salesRep || '',
    });

    deps.getQuotationDetailsForEdit(quotation.uuid);

    deps.setShowTransactionDialog(false);

    this.messageService.add({
      severity: 'info',
      summary: 'Order Mode',
      detail: `Loaded quotation ${quotation.quotationnumber} for placing order`,
      life: 3000,
    });
  }
}
